/**
 * Long-form guides for SEO. Each article targets one primary long-tail keyword.
 * Render with BlogPost.jsx (structured blocks, no raw HTML injection for body text).
 */

export const blogCategories = ['Weight Loss', 'Diet Tips', 'Indian Meals', 'Habits'];

const blocks = {
  howToLoseWeightWithoutCountingCalories: [
    {
      type: 'p',
      text: 'If you have ever opened a calorie-tracking app, typed “one bowl of dal” into a search bar, guessed the portion, and felt quietly exhausted, you are not alone. Millions of people in India want sustainable fat loss, but the logging workflow clashes with thalis, shared meals, office canteens, and busy evenings at home. Learning how to lose weight without counting calories is not about ignoring science—it is about replacing fragile precision with robust habits that survive a real Indian week.',
    },
    {
      type: 'p',
      text: 'Calorie awareness still matters in the background: fat loss usually requires a consistent energy deficit over time. What changes is how you create that deficit. Instead of measuring every gram, you use repeatable meal templates, protein at each eating occasion, vegetable volume, and simple rules for staples like roti and rice. That approach tracks extremely well with Indian home food, where the same dishes repeat and small tweaks compound.',
    },
    {
      type: 'img',
      src: 'https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=1200',
      alt: 'Fresh vegetables and ingredients on a table, representing balanced home-cooked Indian meals without calorie tracking',
      caption: 'Focus on repeatable meals you will actually cook—not perfect logs.',
    },
    {
      type: 'h2',
      text: "Why calorie counting breaks down for many Indian households",
    },
    {
      type: 'p',
      text: 'Indian meals are often mixed: dal mixed with rice, sabzi cooked into gravy, extra tastings while cooking, and spontaneous sweets on festivals. Logging turns each moment into a moral scorecard. When life gets busy, the app becomes the first thing people abandon—not the goal of feeling healthier. That is why a no-tracking diet that relies on structure beats an ideal spreadsheet you cannot maintain.',
    },
    {
      type: 'ul',
      items: [
        'Shared plates make “exact portions” hard to estimate honestly.',
        'Regional recipes vary oil, coconut, and tempering—databases rarely match home reality.',
        'Night shifts, travel, and family dinners introduce unpredictability.',
        'Perfection triggers guilt; guilt triggers quitting.',
      ],
    },
    {
      type: 'h2',
      text: 'The 4-part framework that replaces daily logging',
    },
    {
      type: 'h3',
      text: '1) Anchor every meal with protein',
    },
    {
      type: 'p',
      text: 'Eggs, paneer, curd, dal, chicken, fish, soya, and legumes increase fullness and protect muscle while you lose fat. This is one of the highest-leverage habits for weight loss without calorie counting because it naturally reduces grazing and makes vegetarian Indian plates more satisfying.',
    },
    {
      type: 'h3',
      text: '2) Use simple starch guides for roti and rice',
    },
    {
      type: 'p',
      text: 'You do not need a scale to decide whether tonight is a two-roti night or a smaller rice night. You need a default that matches your goal and activity. Hand-based guides and consistent weekday templates remove decision fatigue. Over a month, the average intake steers itself toward a deficit if your defaults are sane.',
    },
    {
      type: 'h3',
      text: '3) Add vegetables for volume, not punishment',
    },
    {
      type: 'p',
      text: 'Sabzi, salad, cucumber raita, and soups increase meal size without making you feel deprived. This is especially helpful when oil in tadka is hard to quantify—vegetable volume still improves satiety and micronutrient quality.',
    },
    {
      type: 'h3',
      text: '4) Repeat a “good enough” weekday',
    },
    {
      type: 'p',
      text: 'Monday through Friday does not need novelty. Repetition improves adherence. Pick breakfast and snack patterns you enjoy, rotate only lunch and dinner proteins if you get bored, and protect sleep plus steps as your non-negotiables.',
    },
    {
      type: 'h2',
      text: 'Common mistakes when people try “intuitive eating”',
    },
    {
      type: 'p',
      text: 'Some people hear “no tracking” and assume “no boundaries.” Structure is still required—you simply express boundaries as meal patterns instead of digits. Skipping vegetables, under-eating protein, overdoing fried snacks, or treating weekends as a full reset will stall progress even if weekdays feel virtuous.',
    },
    {
      type: 'p',
      text: 'If you want accountability without an app, use weekly check-ins: same outfit photo, waist tape measurement, energy level, and one honest review of whether you followed your plan most days. Trends over two to three weeks matter more than single days.',
    },
    {
      type: 'h2',
      text: 'Practical weekly rhythm for Indian offices and students',
    },
    {
      type: 'p',
      text: 'Most people do not fail on Monday—they fail during weeks when deadlines stack, children fall sick, or relatives visit unexpectedly. A sustainable approach builds “minimum viable discipline”: a breakfast you can assemble in ten minutes, a lunch box pattern that your office microwave can handle, and two dinner templates that rotate without boredom. When the week wobbles, you protect the anchors (protein plus vegetables) rather than chasing perfect numbers. Over eight to twelve weeks, those anchors create the same deficit logging would reveal—without the emotional tax of red and green bars in an app.',
    },
    {
      type: 'p',
      text: 'Hydration, sleep, and stress are not separate from fat loss; they change appetite signals. In Indian cities, long commutes and late dinners push people toward chai-samosa cycles. Fixing the timing of dinner—even by thirty minutes—often improves morning hunger and reduces “second dinner” snacking. Pair that with a structured lunch that includes dal or eggs, and the afternoon energy crash becomes easier to manage without another cup of sugary coffee.',
    },
    {
      type: 'h2',
      text: 'How Notrack helps you lose weight without counting calories',
    },
    {
      type: 'p',
      text: 'Notrack is built for Indian kitchens and unpredictable weeks. You get a daily structure—meal ideas, habit cues, and guidance—without turning every meal into data entry. If you want early access, join the waitlist and lock founding pricing so you can start with a system designed for consistency, not perfection.',
    },
    {
      type: 'p',
      text: 'Related reading: explore our guides on a simple Indian diet plan for weight loss and how to lose weight with home food to connect these habits to the foods you already cook.',
    },
  ],

  simpleIndianDietPlan: [
    {
      type: 'p',
      text: 'A simple Indian diet plan for weight loss should feel boring on paper and effective in real life. The goal is not to impress anyone with exotic superfoods—it is to rotate staples you already buy, cook quickly, and actually eat when work runs late. When a plan respects ghar ka khana, adherence improves dramatically compared with imported meal plans that ignore roti, dal, and regional tastes.',
    },
    {
      type: 'p',
      text: 'The best versions focus on protein distribution across the day, vegetable volume, sensible cooking fat, and predictable portions for rice or roti. They also include chai and snacks in a controlled way so you are not “on diet” for twelve hours and “off diet” every evening.',
    },
    {
      type: 'h2',
      text: 'Sample weekday structure (customise to your region)',
    },
    {
      type: 'p',
      text: 'Think in templates, not identical menus. Swap spices and vegetables freely. Keep the architecture: breakfast with protein, lunch with dal plus staple plus sabzi, dinner lighter or similar depending on activity, and one snack that is planned rather than reactive.',
    },
    {
      type: 'h3',
      text: 'Breakfast options',
    },
    {
      type: 'ul',
      items: [
        'Eggs with vegetables and toast or roti',
        'Paneer bhurji with roti',
        'Curd with nuts and fruit plus a small carb if needed',
        'Idli/dosa with sambhar—add curd, chutney mindfully, and consider extra protein',
      ],
    },
    {
      type: 'h3',
      text: 'Lunch plate',
    },
    {
      type: 'ul',
      items: [
        'Dal or rajma/chhole for plant protein',
        'Sabzi for fibre',
        'Rice or roti—pick the amount that matches your training day',
        'Optional curd or salad',
      ],
    },
    {
      type: 'h3',
      text: 'Dinner',
    },
    {
      type: 'p',
      text: 'Many Indian families eat lighter at night; others prefer roti-based dinners. Either works if the day’s total pattern stays consistent. If dinner is late after commute, split protein earlier so you are not ravenous at 11 p.m.',
    },
    {
      type: 'h2',
      text: 'Indian foods that support satiety',
    },
    {
      type: 'p',
      text: 'Moong dal, masoor dal, chana, rajma, chole, eggs, chicken, fish, paneer, tofu, and soya granules are excellent anchors. Pair them with millets or rice depending on preference. These combinations stabilise blood sugar better than meals that are mostly refined starch with little protein.',
    },
    {
      type: 'h2',
      text: 'What to reduce without feeling deprived',
    },
    {
      type: 'p',
      text: 'Deep-fried starters, heavy mithai every evening, sugary beverages, and late-night packaged snacks are the usual drivers of hidden calories. You do not need to eliminate festival sweets—context matters. On normal weekdays, reduce the easy wins first: visible oil, second servings of rice eaten out of habit, and mindless nibbling while streaming.',
    },
    {
      type: 'h2',
      text: 'Vegetarian and vegan considerations',
    },
    {
      type: 'p',
      text: 'Plant-based Indian diets work well when you combine pulses with grains or dairy alternatives thoughtfully and avoid relying only on potato-heavy sabzi. Rotate protein sources and include nuts, seeds, and curd where suitable so energy stays steady through long workdays.',
    },
    {
      type: 'h2',
      text: 'Exercise and steps: the supporting cast',
    },
    {
      type: 'p',
      text: 'A simple diet plan in India succeeds faster with daily movement—walking after meals, stairs, or basic strength training twice a week. You do not need a gym membership; you need repeatability. Movement improves appetite regulation and makes higher-protein meals feel even more satisfying.',
    },
    {
      type: 'h2',
      text: 'Regional swaps that keep the same structure',
    },
    {
      type: 'p',
      text: 'South, west, north, or east—swap the starch or spice pattern, not the architecture. A plate can still be dal-forward with a moderate serving of rice, or roti-forward with a thick vegetable sabzi, depending on what your family already prefers. Fish and seafood on coasts, mustard oil versus groundnut oil, and seasonal vegetables all fit as long as frying is not the default cooking method for every meal. The psychological win is cultural familiarity: you are not “on a foreign diet”; you are adjusting a home pattern so it supports energy and satiety.',
    },
    {
      type: 'p',
      text: 'Festivals do not cancel structure; they compress it. Eat intentionally at the main event, avoid grazing across six sweet counters, and return to your weekday template as soon as normal life resumes. That single behavioural skill—resuming quickly—predicts long-term weight trends more accurately than perfect weekdays followed by apology weekends.',
    },
    {
      type: 'h2',
      text: 'Build your plan with Notrack',
    },
    {
      type: 'p',
      text: 'Notrack is designed to turn these principles into a clear daily guide for Indian households—without calorie logging. If you want structured meal guidance, habit support, and early-user pricing, join Notrack from our homepage and secure your spot while we are in private beta.',
    },
    {
      type: 'p',
      text: 'Internal tip: pair this article with our guide on diet plans without calorie tracking if you want habit-level detail beyond sample plates.',
    },
  ],

  dietPlanWithoutCalorieTracking: [
    {
      type: 'p',
      text: 'A diet plan without calorie tracking still needs boundaries. The difference is that boundaries are expressed as behaviours—meal order, plate composition, shopping lists, and weekly rhythms—rather than a running tally of kilocalories. For Indian users, this often matches psychology better: you focus on cooking once, eating twice, and protecting staples you culturally enjoy.',
    },
    {
      type: 'p',
      text: 'The misunderstanding to avoid is believing “no tracking” means “no discipline.” Discipline simply moves from arithmetic to architecture. You engineer your environment so the default meal is leaner, higher in protein, and lower in accidental calories from fried snacks and sweet drinks.',
    },
    {
      type: 'h2',
      text: 'Three pillars of a no-tracking diet that works',
    },
    {
      type: 'h3',
      text: 'Pillar A: Meal sequencing',
    },
    {
      type: 'p',
      text: 'Start lunch and dinner with protein and vegetables when possible, then add rice or roti. Sequencing does not require an app; it gently reduces the chance you overfill on starch before you have eaten enough protein and fibre.',
    },
    {
      type: 'h3',
      text: 'Pillar B: Portion language you can teach your family',
    },
    {
      type: 'p',
      text: 'Instead of “200 grams,” use household language: one katori dal depth, roti count, or a bowl size everyone recognises. Families that share language stick to plans longer because elders and children understand the same cues.',
    },
    {
      type: 'h3',
      text: 'Pillar C: Time-based guardrails',
    },
    {
      type: 'p',
      text: 'A planned snack prevents emergency vada-pav decisions. A consistent dinner window reduces late-night hunger spikes. You are not fasting by dogma—you are reducing chaotic eating moments that happen when blood sugar crashes.',
    },
    {
      type: 'h2',
      text: 'Why habits beat spreadsheets for long-tail consistency',
    },
    {
      type: 'p',
      text: 'Google searches for diet help spike after failures: festivals, weddings, exams, job changes. Those are exactly the weeks calorie logs fail. Habit plans survive because they tolerate imperfection. Miss one meal? The next meal still follows the sequence. Miss a day? Monday still has the same breakfast template.',
    },
    {
      type: 'ul',
      items: [
        'Sleep 7+ hours when possible—sleep debt increases cravings.',
        'Protein at breakfast reduces evening binge risk for many office workers.',
        'Carry a water bottle; thirst is often mistaken for hunger.',
        'Keep fruit visible; make ultra-processed snacks inconvenient.',
      ],
    },
    {
      type: 'h2',
      text: 'Tracking progress without logging food',
    },
    {
      type: 'p',
      text: 'Use weekly photos, waist circumference, energy, and grooming cues (belt hole, old jeans). If four indicators move in the same direction across three weeks, your deficit exists. If not, adjust one lever—usually snack quality, evening starch, or protein at breakfast—rather than rebuilding the entire diet.',
    },
    {
      type: 'h2',
      text: 'Indian context: festivals and family pressure',
    },
    {
      type: 'p',
      text: 'You can participate socially without abandoning structure. Eat smaller daytime meals before a wedding dinner, prioritise protein when buffets offer it, and return to your weekday rhythm immediately after. The moral story “I ruined everything” is what stalls fat loss—not the ladoos themselves.',
    },
    {
      type: 'h2',
      text: 'Design your environment for fewer decisions',
    },
    {
      type: 'p',
      text: 'Environment design beats motivation. Keep roasted chana or makhana where you used to keep biscuits. Store sweets in opaque containers on a higher shelf. Fill the fridge drawer with washed cucumbers, carrots, and cherry tomatoes. Pre-boil eggs twice a week. These micro-changes reduce friction on days when willpower is low—the exact days calorie trackers fail because people skip logging rather than face uncomfortable numbers.',
    },
    {
      type: 'p',
      text: 'Social environments matter in India: office tea breaks, relatives insisting on second helpings, and children requesting fast food. Scripts help: “I am full; the dal was excellent” works better than defending a diet ideology. Notrack’s philosophy is non-judgmental structure—fewer debates, more repeatable meals—so you can stay aligned without negotiating every invitation.',
    },
    {
      type: 'h2',
      text: 'Where Notrack fits',
    },
    {
      type: 'p',
      text: 'Notrack packages these pillars into daily guidance tailored for Indian meals—roti, rice, dal, eggs, chicken, paneer—without asking you to track calories. It is built for conversion: if the approach resonates, lock early access on the homepage, join the waitlist, and help us prioritise features for your city and schedule.',
    },
    {
      type: 'p',
      text: 'Cross-link: compare this framework with how to lose weight without counting calories for a step-by-step mindset shift.',
    },
  ],

  howToLoseWeightWithHomeFood: [
    {
      type: 'p',
      text: 'Learning how to lose weight with home food is one of the smartest bets for Indian professionals and parents. You control oil, sugar, and serving sizes more than you would at restaurants, and you repeat meals often enough to turn small tweaks into measurable change. The obstacle is rarely “healthy recipes on Instagram”; it is making defaults at home slightly leaner without starting family arguments.',
    },
    {
      type: 'p',
      text: 'Home food weight loss is not about replacing your mother’s cooking with salads. It is about shifting the weekly pattern: more grilled or pan-cooked items, more dal and sabzi with real fibre, smarter defaults for rice and roti on sedentary days, and fewer invisible calories from frying, tasting, and finishing leftover sweets nightly.',
    },
    {
      type: 'h2',
      text: 'Make one change per week instead of ten',
    },
    {
      type: 'p',
      text: 'Aggressive overhauls collapse when work stress returns. Pick one weekly upgrade: switch an evening snack to fruit plus curd, add a second vegetable to lunch, or standardise breakfast protein. Stack wins slowly so the household adapts.',
    },
    {
      type: 'h2',
      text: 'Cooking techniques that cut calories without cutting culture',
    },
    {
      type: 'ul',
      items: [
        'Air-fry or oven-roast instead of deep-frying where possible.',
        'Measure cooking oil for a week to recalibrate “a little extra” habits.',
        'Use fuller spice profiles so reduced cream still tastes rich.',
        'Batch-cook dal and channa to prevent emergency ordered food.',
      ],
    },
    {
      type: 'h2',
      text: 'Staples that belong in a home-food fat-loss phase',
    },
    {
      type: 'p',
      text: 'Roti, rice, dal, curd, seasonal sabzi, eggs, lean meats, paneer, and millets can all stay. Success comes from pairing staples with protein and vegetables before debating whether rice is “bad.” For many Indians, rice at lunch and lighter roti at dinner—or the reverse—works well when matched to activity.',
    },
    {
      type: 'h2',
      text: 'Snacking at home: the silent saboteur',
    },
    {
      type: 'p',
      text: 'Work-from-home puts the kitchen three metres away. Keep cut fruit, roasted chana, and curd accessible; store high-calorie snacks out of line-of-sight. If children expect packaged treats, separate “their shelf” from “your default shelf” so adulthood weight goals do not feel like constant deprivation.',
    },
    {
      type: 'h2',
      text: 'Eating with family without cooking separate exotic meals',
    },
    {
      type: 'p',
      text: 'Serve shared sabzi and dal, then adjust your personal plate ratios. Ask for one extra portion of protein, one fewer roti if needed, or smaller rice initially with the option to add. Most families accept plate-level changes easier than entirely separate menus.',
    },
    {
      type: 'h2',
      text: 'Sleep, stress, and home routines',
    },
    {
      type: 'p',
      text: 'Late-night screen time and irregular sleep push people toward chai-biscuit cycles and second dinners. Fixing bedtime by thirty to sixty minutes often improves appetite more than swapping roti for salad. Treat sleep as part of your weight plan, especially if you commute long hours in metros.',
    },
    {
      type: 'h2',
      text: 'Batch cooking and storage tricks Indian kitchens already use',
    },
    {
      type: 'p',
      text: 'Pressure cookers, dabbas, and freezers are advantages. Doubling dal and freezing half removes the “no time to cook” excuse that drives online ordering. Roasted jeera powder, ginger-garlic paste, and tomato puree reduce active cooking time on weeknights while keeping flavours familiar. When healthy choices are faster than unhealthy ones, behaviour changes without lectures.',
    },
    {
      type: 'p',
      text: 'Temperature and texture also matter for satisfaction: warm dal with a squeeze of lemon, raita with roasted cumin, and a crunchy salad improve fullness signals. People who feel deprived often need more sensory variety, not smaller portions across the board. Home cooking gives you that variety cheaply compared with repetitive packaged “diet meals.”',
    },
    {
      type: 'h2',
      text: 'Use Notrack to keep home-food habits on track',
    },
    {
      type: 'p',
      text: 'Notrack focuses on consistency with Indian home meals—not calorie tracking. You get daily guidance you can follow while living with family, working irregular hours, and travelling domestically. Join early access from our landing page to lock pricing and get updates when we open the private beta.',
    },
    {
      type: 'p',
      text: 'Next read: our simple Indian diet plan for weight loss ties these home defaults into full-day examples.',
    },
  ],
};

const hero = {
  weightLoss: {
    src: 'https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=1200',
    alt: 'Colourful vegetables and healthy ingredients for sustainable weight loss without counting calories',
  },
  indianMeals: {
    src: 'https://images.pexels.com/photos/6287769/pexels-photo-6287769.jpeg?auto=compress&cs=tinysrgb&w=1200',
    alt: 'Indian-style meal with rice, dal, and vegetables on a dining table',
  },
  dietTips: {
    src: 'https://images.pexels.com/photos/1640774/pexels-photo-1640774.jpeg?auto=compress&cs=tinysrgb&w=1200',
    alt: 'Healthy lunch bowl with grains, protein, and vegetables for habit-based eating',
  },
  homeFood: {
    src: 'https://images.pexels.com/photos/1640772/pexels-photo-1640772.jpeg?auto=compress&cs=tinysrgb&w=1200',
    alt: 'Home kitchen spread with wholesome food for losing weight with home-cooked Indian meals',
  },
};

export const blogPosts = [
  {
    slug: 'how-to-lose-weight-without-counting-calories',
    title: 'How to Lose Weight Without Counting Calories (India-Friendly Guide)',
    metaTitle: 'How to Lose Weight Without Counting Calories | Notrack India',
    metaDescription:
      'Learn how to lose weight without counting calories using simple Indian meals, protein anchors, and habit structure—no food logging required. Join Notrack early access.',
    category: 'Weight Loss',
    primaryKeyword: 'how to lose weight without counting calories',
    readTime: '9 min read',
    published: '2026-02-01',
    heroImage: hero.weightLoss,
    relatedSlugs: [
      'diet-plan-without-calorie-tracking',
      'simple-indian-diet-plan-for-weight-loss',
      'how-to-lose-weight-with-home-food',
    ],
    content: blocks.howToLoseWeightWithoutCountingCalories,
  },
  {
    slug: 'simple-indian-diet-plan-for-weight-loss',
    title: 'Simple Indian Diet Plan for Weight Loss That You Can Actually Cook',
    metaTitle: 'Simple Indian Diet Plan for Weight Loss | Notrack',
    metaDescription:
      'Build a simple Indian diet plan for weight loss with dal, roti, rice, and protein—practical plates for busy weekdays. Start with Notrack’s no-tracking approach.',
    category: 'Indian Meals',
    primaryKeyword: 'simple indian diet plan for weight loss',
    readTime: '8 min read',
    published: '2026-02-05',
    heroImage: hero.indianMeals,
    relatedSlugs: [
      'how-to-lose-weight-with-home-food',
      'how-to-lose-weight-without-counting-calories',
      'diet-plan-without-calorie-tracking',
    ],
    content: blocks.simpleIndianDietPlan,
  },
  {
    slug: 'diet-plan-without-calorie-tracking',
    title: 'Diet Plan Without Calorie Tracking: Habits That Replace the App',
    metaTitle: 'Diet Plan Without Calorie Tracking | Notrack',
    metaDescription:
      'Design a diet plan without calorie tracking—meal sequencing, portion language, and Indian-friendly defaults. Lock early access to Notrack.',
    category: 'Diet Tips',
    primaryKeyword: 'diet plan without calorie tracking',
    readTime: '8 min read',
    published: '2026-02-10',
    heroImage: hero.dietTips,
    relatedSlugs: [
      'how-to-lose-weight-without-counting-calories',
      'simple-indian-diet-plan-for-weight-loss',
      'how-to-lose-weight-with-home-food',
    ],
    content: blocks.dietPlanWithoutCalorieTracking,
  },
  {
    slug: 'how-to-lose-weight-with-home-food',
    title: 'How to Lose Weight With Home Food (Without Fancy Diet Products)',
    metaTitle: 'How to Lose Weight With Home Food | Notrack India',
    metaDescription:
      'Lose weight with home food: smarter cooking defaults, family-friendly plates, and snacks that fit Indian kitchens. Join the Notrack waitlist today.',
    category: 'Habits',
    primaryKeyword: 'how to lose weight with home food',
    readTime: '8 min read',
    published: '2026-02-14',
    heroImage: hero.homeFood,
    relatedSlugs: [
      'simple-indian-diet-plan-for-weight-loss',
      'how-to-lose-weight-without-counting-calories',
      'diet-plan-without-calorie-tracking',
    ],
    content: blocks.howToLoseWeightWithHomeFood,
  },
];

export function getPostBySlug(slug) {
  return blogPosts.find((p) => p.slug === slug) || null;
}

export function getPostsByCategory(category) {
  return blogPosts.filter((p) => p.category === category);
}

/** Newest first — use for /blog index and sitemaps that need deterministic order. */
export function getSortedBlogPosts() {
  return [...blogPosts].sort((a, b) => (a.published < b.published ? 1 : a.published > b.published ? -1 : 0));
}
